<template>
    <div>
        <section aria-labelledby="workload-summaries-heading">
            <h2
                id="workload-summaries-heading"
                class="visually-hidden"
            >
                Summaries
            </h2>
            <WorkloadSummaries
                title=""
                :assignment-summaries-data="assignmentSummariesData"
            />
        </section>

        <section aria-labelledby="charts-heading">
            <div class="charts-container">
                <OperationsInsightsChart
                    title="Assignment Age by Workload"
                    :chart-options="workloadAgeChartOptions"
                    :is-loading="isLoading"
                    :error="blAgeBandsWorkloadsError"
                />
                <OperationsInsightsChart
                    title="Assignments In/Out"
                    :chart-options="assignmentsInOutChartOptions"
                    :is-loading="assignmentsLoading"
                    :error="assignmentsError"
                />
            </div>
        </section>

        <section aria-labelledby="charts-heading">
            <div class="grid-container-wrapper">
                <div class="grid-container">
                    <OperationsInsightsGrid
                        title="Assignment Age by Workload"
                        :column-defs="assignmentAgeByWorkloadColumnDefs"
                        :row-data="assignmentAgeByWorkloadGridData"
                        :error="assignmentsError"
                    />
                </div>
                <div class="grid-container">
                    <OperationsInsightsGrid
                        title="Total Assignments by Workload"
                        :column-defs="totalAssignmentsByWorkloadColumnDefs"
                        :row-data="totalAssignmentsByWorkloadGridData"
                        :error="assignmentsError"
                    />
                </div>
            </div>
        </section>

        <section aria-labelledby="charts-heading">
            <div class="charts-container">
                <OperationsInsightsChart
                    title="Open Assignments by Workload"
                    :chart-options="openAssignmentsByWorkloadChartOptions"
                    :is-loading="isLoading"
                    :error="blAgeBandsWorkloadsError"
                />
                <OperationsInsightsChart
                    title="Assignment Completion on Time by Workload"
                    :chart-options="assignmentsCompletionTimeByWorkloadChartOptions"
                    :is-loading="assignmentsLoading"
                    :error="assignmentsError"
                />
            </div>
        </section>

        <section aria-labelledby="charts-heading">
            <div class="grid-container-wrapper">
                <div class="grid-container">
                    <OperationsInsightsGrid
                        title="Assignment Health by Workload"
                        :column-defs="assignmentHealthByWorkloadColumnDefs"
                        :row-data="assignmentHealthByWorkloadGridData"
                        :error="assignmentsError"
                    />
                </div>
                <div class="grid-container">
                    <OperationsInsightsGrid
                        title="Average Assignment Completion Time by Workload"
                        :column-defs="assignmentAvgCompletedTimeByWorkloadColumnDefs"
                        :row-data="assignmentAvgCompletedTimeByWorkloadGridData"
                        :error="assignmentsError"
                    />
                </div>
            </div>
        </section>

        <OperationsInsightsGrid
            title="Assignment Summary"
            :column-defs="assignmentSummaryByWorkloadColumnDefs"
            :row-data="assignmentSummaryByWorkloadGridData"
            :error="assignmentsError"
        />
    </div>
</template>

<script setup lang="ts">
import { watch, toRef } from 'vue';
import { ApiQueryParams } from '@/pages/OperationsInsights/types/ApiQueryParams';
import { useLogger } from '@agyletime/vue-3-common/src/composables/logger';
import OperationsInsightsChart from '@/pages/OperationsInsights/components/OperationsInsightsChart.vue';
import { useAssignmentsAgeBandsWorkloadsData } from '@/pages/OperationsInsights/composables/useAssignmentsAgeBandsWorkloadsData';
import { useAssignmentsByWorkloadsData } from '@/pages/OperationsInsights/composables/useAssignmentsByWorkloadsData';
import { useWorkloadChartConfigs } from '@/pages/OperationsInsights/composables/useWorkloadChartConfigs';
import OperationsInsightsGrid from '@/pages/OperationsInsights/components/OperationsInsightsGrid.vue';
import WorkloadSummaries from '@/pages/OperationsInsights/components/WorkloadSummaries.vue';
interface Props {
    queryParams: ApiQueryParams;
}

const props = defineProps<Props>();
const logger = useLogger('WorkloadInsightsTab');
const queryParamsRef = toRef(props, 'queryParams');

watch(
    queryParamsRef,
    (newParams) => {
        logger.debug('queryParams changed', newParams);
    },
    { deep: true, immediate: true }
);

const {
    workloadChartSeries,
    assignmentAgeByWorkloadColumnDefs,
    assignmentAgeByWorkloadGridData,
    isLoading,
    error: blAgeBandsWorkloadsError
} = useAssignmentsAgeBandsWorkloadsData(queryParamsRef);

const {
    assignmentSummariesData,
    assignmentsInOutChartSeries,
    totalAssignmentsByWorkloadColumnDefs,
    totalAssignmentsByWorkloadGridData,
    openAssignmentsByWorkloadChartSeries,
    assignmentsCompletionTimeByWorkloadChartSeries,
    assignmentHealthByWorkloadColumnDefs,
    assignmentHealthByWorkloadGridData,
    assignmentAvgCompletedTimeByWorkloadColumnDefs,
    assignmentAvgCompletedTimeByWorkloadGridData,
    assignmentSummaryByWorkloadColumnDefs,
    assignmentSummaryByWorkloadGridData,
    isLoading: assignmentsLoading,
    error: assignmentsError
} = useAssignmentsByWorkloadsData(queryParamsRef);

const {
    workloadAgeChartOptions,
    assignmentsInOutChartOptions,
    openAssignmentsByWorkloadChartOptions,
    assignmentsCompletionTimeByWorkloadChartOptions
} = useWorkloadChartConfigs(
    workloadChartSeries,
    assignmentsInOutChartSeries,
    openAssignmentsByWorkloadChartSeries,
    assignmentsCompletionTimeByWorkloadChartSeries
);
</script>

<style scoped lang="scss">
@use '../styles/metric-card.scss';
@use '../styles/workstream-summaries-layout.scss';
</style>
